<template>
    <TailwindLayout title="Cloud IP Whitelist">
        <div class="-mx -mt-10 space-y-6">
            <!-- Header band -->
            <div class="relative overflow-hidden rounded-2xl border border-rent-risk-blue-100 bg-gradient-to-br from-rent-risk-blue-100 via-white to-white px-6 py-7 sm:px-8">
                <div class="flex items-center gap-4">
                    <span class="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-rent-risk-blue-400 text-white shadow-sm">
                        <font-awesome-icon :icon="faNetworkWired" class="h-7 w-7" />
                    </span>
                    <div>
                        <h1 class="text-3xl font-bold tracking-tight text-rent-risk-blue-400">Cloud IP Whitelist</h1>
                        <p class="mt-1 max-w-2xl text-sm text-gray-600">
                            Laravel Cloud outbound IPs for <span class="font-semibold">{{ region }}</span>. Acknowledge each IP
                            once you've confirmed it's in the Experian whitelist — the daily monitor only alerts for
                            published IPs that aren't acknowledged here.
                        </p>
                        <p v-if="lastCheckedAt" class="mt-1 text-xs text-gray-400">
                            Last checked {{ lastCheckedAt }}<span v-if="egressIp"> · last egress IP {{ egressIp }}</span>
                        </p>
                    </div>
                </div>
            </div>

            <!-- Status banner -->
            <div
                class="flex flex-col gap-3 rounded-2xl border p-5 sm:flex-row sm:items-center sm:justify-between"
                :class="unacknowledgedCount > 0
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-emerald-200 bg-emerald-50'"
            >
                <div class="flex items-start gap-3">
                    <font-awesome-icon
                        :icon="unacknowledgedCount > 0 ? faTriangleExclamation : faCircleCheck"
                        class="mt-0.5 h-5 w-5"
                        :class="unacknowledgedCount > 0 ? 'text-amber-500' : 'text-emerald-500'"
                    />
                    <div>
                        <p class="text-sm font-semibold" :class="unacknowledgedCount > 0 ? 'text-amber-800' : 'text-emerald-800'">
                            {{ unacknowledgedCount > 0
                                ? `${unacknowledgedCount} published IP${unacknowledgedCount === 1 ? '' : 's'} not yet acknowledged`
                                : 'All published IPs are acknowledged' }}
                        </p>
                        <p class="mt-0.5 text-xs" :class="unacknowledgedCount > 0 ? 'text-amber-700' : 'text-emerald-700'">
                            {{ unacknowledgedCount > 0
                                ? 'Add each unacknowledged IP to the Experian developer portal, then acknowledge it below.'
                                : 'The monitor will only alert if Laravel Cloud publishes a new IP.' }}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    :disabled="processing !== null"
                    class="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-rent-risk-blue-400 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                    @click="acknowledgeAll"
                >
                    <font-awesome-icon :icon="faCheckDouble" class="h-4 w-4" />
                    Acknowledge all
                </button>
            </div>

            <!-- IP table -->
            <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <table class="min-w-full divide-y divide-gray-200 text-sm">
                    <thead class="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">
                        <tr>
                            <th class="px-5 py-3">IP Address</th>
                            <th class="px-5 py-3">Tags</th>
                            <th class="px-5 py-3">Status</th>
                            <th class="px-5 py-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="row in ips" :key="row.ip_address" class="hover:bg-gray-50">
                            <td class="px-5 py-3 font-mono text-gray-900">
                                {{ row.ip_address }}
                                <span class="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-500">
                                    {{ row.version }}
                                </span>
                            </td>
                            <td class="px-5 py-3">
                                <div class="flex flex-wrap gap-1.5">
                                    <span v-if="row.is_egress" class="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                        Current egress
                                    </span>
                                    <span
                                        v-if="!row.is_published"
                                        class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700"
                                        title="Acknowledged but Laravel Cloud no longer publishes this IP — safe to de-list from Experian."
                                    >
                                        No longer published
                                    </span>
                                </div>
                            </td>
                            <td class="px-5 py-3">
                                <span v-if="row.acknowledged" class="inline-flex items-center gap-1.5 text-emerald-700">
                                    <font-awesome-icon :icon="faCircleCheck" class="h-4 w-4" />
                                    <span>
                                        Acknowledged
                                        <span class="block text-xs text-gray-400">{{ row.acknowledged_by }} · {{ row.acknowledged_at }}</span>
                                    </span>
                                </span>
                                <span v-else class="inline-flex items-center gap-1.5 text-amber-700">
                                    <font-awesome-icon :icon="faTriangleExclamation" class="h-4 w-4" />
                                    Not acknowledged
                                </span>
                            </td>
                            <td class="px-5 py-3 text-right">
                                <button
                                    v-if="!row.acknowledged"
                                    type="button"
                                    :disabled="processing !== null"
                                    class="inline-flex items-center gap-1.5 rounded-lg bg-rent-risk-blue-400 px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                                    @click="acknowledge(row.ip_address)"
                                >
                                    <font-awesome-icon :icon="faCheck" class="h-3 w-3" />
                                    Acknowledge
                                </button>
                                <button
                                    v-else
                                    type="button"
                                    :disabled="processing !== null"
                                    class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-100 disabled:opacity-50"
                                    @click="unacknowledge(row.ip_address)"
                                >
                                    Un-acknowledge
                                </button>
                            </td>
                        </tr>
                        <tr v-if="ips.length === 0">
                            <td colspan="4" class="px-5 py-10 text-center text-sm text-gray-400">
                                No Cloud IP snapshot yet. The daily monitor will populate this once it runs.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- IPv6 footnote: informational only, not tracked for Experian. -->
            <p v-if="ipv6Published.length" class="px-1 text-xs text-gray-400">
                Laravel Cloud also publishes IPv6 ({{ ipv6Published.join(', ') }}). Experian's whitelist is IPv4-only,
                so it isn't tracked here.
            </p>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faNetworkWired,
    faTriangleExclamation,
    faCircleCheck,
    faCheck,
    faCheckDouble,
} from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'

defineProps({
    region: { type: String, required: true },
    lastCheckedAt: { type: String, default: null },
    egressIp: { type: String, default: null },
    ips: { type: Array, default: () => [] },
    ipv6Published: { type: Array, default: () => [] },
    unacknowledgedCount: { type: Number, default: 0 },
})

// Tracks which IP (or 'all') is mid-request so buttons disable during a POST.
const processing = ref(null)

const post = (url, ip, data = {}) => {
    processing.value = ip
    router.post(url, data, {
        preserveScroll: true,
        onFinish: () => {
            processing.value = null
        },
    })
}

const acknowledge = (ip) => post(route('bridge.cloud-ips.acknowledge'), ip, { ip })
const unacknowledge = (ip) => post(route('bridge.cloud-ips.unacknowledge'), ip, { ip })
const acknowledgeAll = () => post(route('bridge.cloud-ips.acknowledge-all'), 'all')
</script>
